/**
 * AiAnalysisCardView：AI 分析卡片（标题 + 操作区 + 内容区）。
 *
 * 核心职责：
 * - 管理“卡片独立会话”与“弹窗 Apply 接管会话”的展示切换；
 * - 维护 promptInfo 的本地工作态（currentPromptInfo），并在编辑态同步回 Layout 持久化；
 * - 处理自动运行：仅在“首次挂载/查询条件变化”时触发，并对 Confirm/Apply 等流程做去重与互斥。
 *
 * 关键交互契约：
 * - Apply 接管：卡片展示直接切到 attachedSession（同一条 SSE），不得自动重跑；
 * - Stop：若卡片当前展示的是 attachedSession，则 stop 必须走上层 controller（由其定位到 sessionId/messageId 做后端 cancel）。
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'tea-component';
import { useIntl } from 'umi';

import CollapseCard from '@/pages/datalab/components/CollapseCard';
import SvgIcon from '@home/components/SvgIcon';

import AiAnalysisCardExtra from './AiAnalysisCardExtra';

import type { IBuildAiAnalysisInputParams } from '../runAiAnalysis';
import { CUSTOM_APPROACH_VALUE } from '../AiSettings/editorDraft';
import AIAnalysisBox from '../AIAnalysisBox';
import type { IAiAnalysisCardProps, IAiAnalysisCardRuntimeApi, IAiPromptInfo } from '../types';
import { useAiAnalysisCardSession } from '../hooks/useAiAnalysisCardSession';

import robotSvg from '@/pages/aigc/pages/Agent/svg/robot.svg';

import './index.scss';

const parseLayoutChartIds = (signature: string): string[] => {
  try {
    const parsed = JSON.parse(signature);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * true：新布局图表集合是在旧集合基础上纯新增（旧 id 均保留）；
 * false：含删图、换图或无图→有图，需按业务重置卡片态。
 */
const isLayoutChartPureAddition = (prevSignature: string, nextSignature: string): boolean => {
  const prevIds = parseLayoutChartIds(prevSignature);
  const nextIds = parseLayoutChartIds(nextSignature);
  if (prevIds.length === 0 || nextIds.length <= prevIds.length) {
    return false;
  }
  const nextIdSet = new Set(nextIds);
  return prevIds.every((id) => nextIdSet.has(id));
};

const AiAnalysisCardView: React.FC<IAiAnalysisCardProps> = (props) => {
  const {
    onStopSession,
    onReleaseAttachedSession,
    onRunAttachedAnalysis,
    isEdit,
    shouldAutoRunOnMount = true,
    onDelete,
    gameCode,
    dashboardId,
    cardId,
    attachedCardId,
    attachedSession,
    taskIds: allTaskIdsFromLayout = [],
    queries,
    promptOptions,
    promptInfo,
    onPromptInfoChange,
    onOpenAiAnalysisModal,
    onRegisterRuntimeApi,
    onCloseAiAnalysisModal,
  } = props;

  const { formatMessage } = useIntl();
  const {
    session: cardSession,
    runAnalysis: runCardSessionAnalysis,
    stopAnalysis: stopCardSessionAnalysis,
    setAiMetricsCallback: setCardAiMetricsCallback,
  } = useAiAnalysisCardSession({
    gameCode,
    dashboardId,
    queries,
    formatMessage,
  });

  const isSessionAttached = attachedCardId === cardId;
  const displaySession = isSessionAttached ? (attachedSession ?? cardSession) : cardSession;

  // ===== 交互状态：当前分析思路（可编辑态）与提示条 =====
  // 当前卡片正在使用的 prompt 配置（本地工作态，可先于上层持久化更新）。
  const [currentPromptInfo, setCurrentPromptInfo] = useState<IAiPromptInfo | undefined>(promptInfo);
  const currentPromptInfoRef = useRef<IAiPromptInfo | undefined>(promptInfo);
  // 供操作区下拉/展示使用的 promptId，避免每次都从复杂对象中解析。
  const [promptId, setPromptId] = useState<number | undefined>(promptInfo?.promptId);
  // “筛选条件已变更，是否刷新分析”提示条的显隐状态。
  const [isFilterChangeTipVisible, setIsFilterChangeTipVisible] = useState<boolean>(false);

  // ===== 自动运行去重：避免“Confirm 更新 promptInfo”触发二次请求 =====
  const hasAutoRunRef = useRef<boolean>(false);
  const lastAnalyzedQuerySignatureRef = useRef<string>('');
  /** 布局图表 id 集合签名，用于检测拖入/删除图表 */
  const prevLayoutChartSignatureRef = useRef<string>('');
  /** 跳过首次快照，避免挂载时误判为图表变更 */
  const isLayoutChartSignatureInitializedRef = useRef<boolean>(false);

  // 将最新 currentPromptInfo 同步到 ref，供 runtimeApi 等异步场景读取。
  useEffect(() => {
    currentPromptInfoRef.current = currentPromptInfo;
  }, [currentPromptInfo]);

  // 外部 promptInfo 变更时，重置卡片本地态并同步 ref，保证内外状态一致。
  useEffect(() => {
    setCurrentPromptInfo(promptInfo);
    setPromptId(promptInfo?.promptId);
    // 同步 ref，避免 controller 立刻读取到旧值
    currentPromptInfoRef.current = promptInfo;
  }, [promptInfo]);

  // ===== 派生数据：只关心 filters/user_filter，避免 queries 里其他字段导致无意义重跑 =====
  const querySignature = useMemo(
    () => JSON.stringify({
      filters: queries?.filters ?? null,
      userFilter: queries?.user_filter ?? null,
    }),
    [queries?.filters, queries?.user_filter],
  );

  const canAutoRun = Array.isArray(queries?.filters);
  const hasConversation = displaySession.conversations.length > 0;
  const isSending = displaySession.streamingState === 'pending' || displaySession.streamingState === 'progressing';

  /** true：报表布局中至少有一张图表 */
  const hasLayoutCharts = allTaskIdsFromLayout.length > 0;

  /**
   * true：当前可发起 SSE；
   * false：报表无图或分析范围与布局无交集，展示 NoChart。
   */
  const hasAnalyzableCharts = useMemo(() => {
    if (!hasLayoutCharts) {
      return false;
    }
    const scope = currentPromptInfo?.analysisScope;
    if (scope?.type === 'chart') {
      const layoutIdSet = new Set(allTaskIdsFromLayout);
      return (scope.taskIds || []).some((taskId) => layoutIdSet.has(taskId));
    }
    return true;
  }, [allTaskIdsFromLayout, currentPromptInfo?.analysisScope, hasLayoutCharts]);

  const layoutChartSignature = useMemo(
    () => JSON.stringify([...allTaskIdsFromLayout].sort()),
    [allTaskIdsFromLayout],
  );

  const hasRunnablePrompt = useMemo(() => {
    const normalizedPromptId = Number(currentPromptInfo?.promptId);
    const hasValidPromptId = Number.isFinite(normalizedPromptId) && normalizedPromptId > 0;
    if (currentPromptInfo?.isCustom) {
      return hasValidPromptId || Boolean(
        currentPromptInfo.customPrompt?.promptContent || currentPromptInfo.customPrompt?.analysisContext,
      );
    }
    return hasValidPromptId;
  }, [currentPromptInfo]);

  /**
   * 将 promptInfo 映射为请求参数（在原有分支上扩展：统一带正文，promptId 仅保留在注释中便于回滚）。
   */
  const buildRequestParamsByPromptInfo = useCallback((nextPromptInfo?: IAiPromptInfo): IBuildAiAnalysisInputParams => {
    const scopeTaskIds = nextPromptInfo?.analysisScope?.type === 'all'
      ? allTaskIdsFromLayout
      : (nextPromptInfo?.analysisScope?.taskIds || []);
    if (nextPromptInfo?.isCustom) {
      const hasCustomPrompt = Boolean(
        nextPromptInfo.customPrompt?.promptContent || nextPromptInfo.customPrompt?.analysisContext,
      );
      if (!hasCustomPrompt) {
        return {
          taskIds: scopeTaskIds,
          // promptId: nextPromptInfo?.promptId,
          promptContent: '',
          analysisContext: '',
        };
      }
      return {
        taskIds: scopeTaskIds,
        promptContent: nextPromptInfo.customPrompt?.promptContent,
        analysisContext: nextPromptInfo.customPrompt?.analysisContext,
      };
    }
    const presetId = nextPromptInfo?.promptId;
    const matchedOption = typeof presetId === 'number' && presetId > 0
      ? promptOptions?.find((option) => String(option.value) === String(presetId))
      : undefined;
    return {
      taskIds: scopeTaskIds,
      // promptId: presetId,
      promptContent: String(matchedOption?.info?.promptContent ?? ''),
      analysisContext: String(matchedOption?.info?.analysisContext ?? ''),
    };
  }, [allTaskIdsFromLayout, promptOptions]);

  /**
   * 开跑前的互斥保障：
   * - streaming 中直接发起新请求会造成 answer 混叠；
   * - attachedSession 的取消必须由 controller 统一处理（需要 sessionId/messageId），卡片自身只能提供 fallbackStop。
   */
  const ensureStopBeforeStartNewAnalysis = useCallback(async () => {
    const isSending = displaySession.streamingState === 'pending' || displaySession.streamingState === 'progressing';
    if (!isSending) {
      return;
    }
    if (isSessionAttached) {
      const fallbackStop = () => {
        displaySession.cancelStreaming();
        displaySession.clearConversations();
      };
      onStopSession?.(fallbackStop);
      return;
    }
    await stopCardSessionAnalysis();
  }, [displaySession, isSessionAttached, onStopSession, stopCardSessionAnalysis]);

  // 分析执行统一入口：先保证旧请求停止，再按是否 attached 路由到对应执行链路。
  const runAnalysisByParams = useCallback(async (params: IBuildAiAnalysisInputParams) => {
    if (!hasAnalyzableCharts) {
      return;
    }
    await ensureStopBeforeStartNewAnalysis();
    if (isSessionAttached) {
      await onRunAttachedAnalysis?.(params);
      return;
    }
    // 非 attached 路径必须走 cardSession.runAnalysis，确保每次请求前都会生成新的 session_id。
    await runCardSessionAnalysis(params);
  }, [ensureStopBeforeStartNewAnalysis, hasAnalyzableCharts, isSessionAttached, onRunAttachedAnalysis, runCardSessionAnalysis]);

  // 记录“当前筛选已完成分析”的基线，并关闭筛选变更提示。
  const markQueryAsAnalyzed = useCallback((nextQuerySignature: string) => {
    lastAnalyzedQuerySignatureRef.current = nextQuerySignature;
    setIsFilterChangeTipVisible(false);
  }, []);

  /** 清空结论并阻断自动 SSE，使内容区回到「请确认分析方法」空态 */
  const resetToConfirmApproachState = useCallback(() => {
    onReleaseAttachedSession?.();
    const isCardStreaming = cardSession.streamingState === 'pending'
      || cardSession.streamingState === 'progressing';
    if (isCardStreaming) {
      stopCardSessionAnalysis().catch(() => {
        cardSession.cancelStreaming();
        cardSession.clearConversations();
      });
    } else if (cardSession.conversations.length > 0) {
      cardSession.clearConversations();
    }
    hasAutoRunRef.current = true;
    lastAnalyzedQuerySignatureRef.current = '';
    setIsFilterChangeTipVisible(false);
  }, [cardSession, onReleaseAttachedSession, stopCardSessionAnalysis]);

  // 布局图表变更：删/换图或无图→有图时重置；已确认分析后仅新增图表则保持现有结论。
  useEffect(() => {
    if (!isLayoutChartSignatureInitializedRef.current) {
      isLayoutChartSignatureInitializedRef.current = true;
      prevLayoutChartSignatureRef.current = layoutChartSignature;
      return;
    }
    if (prevLayoutChartSignatureRef.current === layoutChartSignature) {
      return;
    }
    const prevSignature = prevLayoutChartSignatureRef.current;
    const isPureAddition = isLayoutChartPureAddition(prevSignature, layoutChartSignature);
    /**
     * true：用户已确认分析方法且分析已进行或已有结论；
     * 此口径下纯新增图表不应清空会话或回到 Empty。
     */
    const hasEstablishedAnalysis = isSessionAttached
      || hasConversation
      || isSending
      || Boolean(lastAnalyzedQuerySignatureRef.current);
    prevLayoutChartSignatureRef.current = layoutChartSignature;
    if (isPureAddition && hasEstablishedAnalysis) {
      return;
    }
    resetToConfirmApproachState();
  }, [
    hasConversation,
    isSending,
    isSessionAttached,
    layoutChartSignature,
    resetToConfirmApproachState,
  ]);

  // 更新 promptInfo 本地态；编辑模式下同步回上层，保持布局持久化数据一致。
  const setCurrentPromptInfoAndMaybePersist = useCallback((nextPromptInfo: IAiPromptInfo) => {
    const nextPromptId = nextPromptInfo?.promptId;
    // 同步 ref，避免 controller 立刻读取到旧值
    currentPromptInfoRef.current = nextPromptInfo;
    setCurrentPromptInfo(nextPromptInfo);
    setPromptId(nextPromptId);
    if (isEdit) {
      onPromptInfoChange?.(nextPromptInfo);
    }
  }, [isEdit, onPromptInfoChange]);

  // 向 controller 注册卡片 runtimeApi，供弹窗/上层跨组件控制当前卡片能力。
  useEffect(() => {
    if (!onRegisterRuntimeApi) {
      return;
    }
    const api: IAiAnalysisCardRuntimeApi = {
      getCurrentPromptInfo: () => currentPromptInfoRef.current,
      setCurrentPromptInfo: (nextPromptInfo) => setCurrentPromptInfoAndMaybePersist(nextPromptInfo),
      runCardAnalysisByPromptInfo: async (nextPromptInfo) => {
        // 在触发分析前先置位，避免 Confirm 更新 promptInfo 时触发的自动运行 effect 并发重复发起请求。
        hasAutoRunRef.current = true;
        await runAnalysisByParams(buildRequestParamsByPromptInfo(nextPromptInfo));
        markQueryAsAnalyzed(querySignature);
      },
      stopRunningAnalysis: async () => {
        await ensureStopBeforeStartNewAnalysis();
      },
      setAiMetricsCallback: (cb) => {
        setCardAiMetricsCallback(cb);
      },
    };
    onRegisterRuntimeApi(cardId, api);
  }, [
    buildRequestParamsByPromptInfo,
    cardId,
    currentPromptInfoRef,
    markQueryAsAnalyzed,
    onRegisterRuntimeApi,
    querySignature,
    runAnalysisByParams,
    setCurrentPromptInfoAndMaybePersist,
  ]);

  // 自动分析触发器：仅在允许条件满足且未命中去重时执行，避免 Confirm/Apply 引发重复请求。
  useEffect(() => {
    // Apply 接管后，卡片展示的 session 已来自弹窗 preview（可能仍在 streaming 中），此时不得再触发自动分析重跑。
    if (isSessionAttached) {
      return;
    }
    // 仅当「当前筛选」已真正跑过分析时才跳过；空 ref 表示尚未分析（含 apply/get 加载中 promptInfo 为空）。
    // 不可与「筛选提示 baseline」混用同一 ref 预写，否则 apply 配置到达后会被误判为已分析。
    if (
      lastAnalyzedQuerySignatureRef.current
      && lastAnalyzedQuerySignatureRef.current === querySignature
    ) {
      return;
    }
    if (!shouldAutoRunOnMount || hasAutoRunRef.current || !canAutoRun || !hasRunnablePrompt || !hasAnalyzableCharts) {
      return;
    }
    hasAutoRunRef.current = true;
    runAnalysisByParams(buildRequestParamsByPromptInfo(currentPromptInfo))
      .finally(() => {
        markQueryAsAnalyzed(querySignature);
      });
  }, [
    buildRequestParamsByPromptInfo,
    canAutoRun,
    currentPromptInfo,
    hasAnalyzableCharts,
    hasRunnablePrompt,
    isSessionAttached,
    markQueryAsAnalyzed,
    querySignature,
    runAnalysisByParams,
    shouldAutoRunOnMount,
  ]);

  // 维护“筛选变化提示”显隐：发送中不提示，非发送中按“有结果+筛选变更”显示。
  useEffect(() => {
    if (!canAutoRun) {
      return;
    }
    // 1) 任何入口一旦触发重新分析（进入 sending），都不应提示“去刷新”。
    //    同时把“已分析基线”推进到当前筛选，避免请求结束后提示又反弹。
    if (isSending) {
      if (lastAnalyzedQuerySignatureRef.current !== querySignature) {
        markQueryAsAnalyzed(querySignature);
      } else {
        setIsFilterChangeTipVisible(false);
      }
      return;
    }

    // 2) 未在重新分析时：只有在「已有分析基线 + 有会话 + 筛选变化」时才提示手动刷新。
    if (!hasConversation || !lastAnalyzedQuerySignatureRef.current) {
      setIsFilterChangeTipVisible(false);
      return;
    }
    setIsFilterChangeTipVisible(querySignature !== lastAnalyzedQuerySignatureRef.current);
  }, [canAutoRun, hasConversation, isSending, markQueryAsAnalyzed, querySignature]);

  // 统一的打开弹窗入口：根据全屏状态计算弹层容器，并可选传入默认选中的分析思路 ID。
  const openModalWithDefaultSelectedApproachId = useCallback((nextDefaultSelectedApproachId?: number | string) => {
    const fullscreenElement = document.fullscreenElement;
    const popupContainer = fullscreenElement?.contains(fullScreenContentRef.current)
      ? fullScreenContentRef.current
      : undefined;
    onOpenAiAnalysisModal({
      cardId,
      entryMode: isEdit ? 'edit' : 'preview',
      defaultSelectedApproachId: nextDefaultSelectedApproachId,
      popupContainer,
    });
  }, [cardId, isEdit, onOpenAiAnalysisModal]);

  // 卡片主入口点击：打开分析思路设置弹窗，不预选具体分析思路。
  const handleOpenAnalysisApproach = useCallback(() => {
    openModalWithDefaultSelectedApproachId(undefined);
  }, [openModalWithDefaultSelectedApproachId]);

  // 编辑当前分析思路：若是有效预置分析思路则按该分析思路打开，否则回落到“自定义分析思路”。
  const handleEditApproach = useCallback((nextPromptId: string) => {
    const parsedPromptId = Number(nextPromptId);
    if (Number.isFinite(parsedPromptId) && parsedPromptId > 0) {
      openModalWithDefaultSelectedApproachId(parsedPromptId);
    } else {
      openModalWithDefaultSelectedApproachId(CUSTOM_APPROACH_VALUE);
    }
  }, [openModalWithDefaultSelectedApproachId]);

  // 快速切换分析思路并立即触发分析：
  // - 选择“自定义”时优先复用已有 customPrompt；
  // - 选择预置分析思路时保留历史 customPrompt，便于后续切回自定义时回显。
  const handleSelectApproach = useCallback((nextPromptId: string) => {
    if (nextPromptId === CUSTOM_APPROACH_VALUE) {
      const customPrompt = currentPromptInfo?.customPrompt;
      if (customPrompt) {
        const nextPromptInfo: IAiPromptInfo = {
          analysisScope: currentPromptInfo?.analysisScope || {
            type: 'all',
            taskIds: allTaskIdsFromLayout,
          },
          promptId: currentPromptInfo?.promptId,
          isCustom: true,
          customPrompt,
        };
        setCurrentPromptInfoAndMaybePersist(nextPromptInfo);
        runAnalysisByParams(buildRequestParamsByPromptInfo(nextPromptInfo))
          .then(() => {
            markQueryAsAnalyzed(querySignature);
          })
          .catch(() => {});
        return;
      }
      const fallbackPromptId = Number(promptOptions?.[0]?.value);
      if (!Number.isFinite(fallbackPromptId) || fallbackPromptId <= 0) {
        return;
      }
      const fallbackPromptInfo: IAiPromptInfo = {
        analysisScope: currentPromptInfo?.analysisScope || {
          type: 'all',
          taskIds: allTaskIdsFromLayout,
        },
        promptId: fallbackPromptId,
        isCustom: true,
      };
      setCurrentPromptInfoAndMaybePersist(fallbackPromptInfo);
      runAnalysisByParams(buildRequestParamsByPromptInfo(fallbackPromptInfo))
        .then(() => {
          markQueryAsAnalyzed(querySignature);
        })
        .catch(() => {});
      return;
    }

    const normalizedPromptId = Number(nextPromptId);
    if (!Number.isFinite(normalizedPromptId) || normalizedPromptId <= 0) {
      return;
    }

    const nextPromptInfo: IAiPromptInfo = {
      analysisScope: currentPromptInfo?.analysisScope || {
        type: 'all',
        taskIds: allTaskIdsFromLayout,
      },
      promptId: normalizedPromptId,
      isCustom: false,
    };
    // 预览模式下用户可能先编辑了自定义 prompt，再切到预置 prompt 发起分析。
    // 此时需要保留历史 customPrompt，确保后续再切回自定义时弹窗可以正确回显用户输入。
    if (currentPromptInfo?.customPrompt) {
      nextPromptInfo.customPrompt = currentPromptInfo.customPrompt;
    }
    setCurrentPromptInfoAndMaybePersist(nextPromptInfo);
    runAnalysisByParams(buildRequestParamsByPromptInfo(nextPromptInfo))
      .then(() => {
        markQueryAsAnalyzed(querySignature);
      })
      .catch(() => {});
  }, [
    buildRequestParamsByPromptInfo,
    currentPromptInfo?.customPrompt,
    currentPromptInfo?.promptId,
    allTaskIdsFromLayout,
    markQueryAsAnalyzed,
    querySignature,
    promptOptions,
    runAnalysisByParams,
    setCurrentPromptInfoAndMaybePersist,
  ]);

  // 使用当前 prompt 配置发起分析，并把当前筛选标记为“已分析”基线。
  const handleRunAnalysis = useCallback(async (options?: { refresh?: boolean; }) => {
    await runAnalysisByParams({
      ...buildRequestParamsByPromptInfo(currentPromptInfo),
      ...(options?.refresh ? { refresh: true } : {}),
    });
    markQueryAsAnalyzed(querySignature);
  }, [
    buildRequestParamsByPromptInfo,
    currentPromptInfo,
    markQueryAsAnalyzed,
    querySignature,
    runAnalysisByParams,
  ]);

  // 删除卡片入口：透传到上层删除逻辑。
  const handleDeleteCard = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  // 停止分析：若卡片接管了共享会话则走上层停止逻辑，否则停止本卡片会话并兜底执行 fallback。
  const handleStopSession = useCallback((fallbackStop: () => void) => {
    if (isSessionAttached) {
      onStopSession?.(fallbackStop);
      return;
    }
    stopCardSessionAnalysis().catch(() => {
      fallbackStop();
    });
  }, [isSessionAttached, onStopSession, stopCardSessionAnalysis]);

  // 编辑已选分析思路值：按当前 promptInfo 决定弹窗默认选中项（自定义/预置/无预选）。
  const handleEditPresetValue = useCallback(() => {
    if (currentPromptInfo?.isCustom) {
      openModalWithDefaultSelectedApproachId(CUSTOM_APPROACH_VALUE);
    } else {
      const normalizedPromptId = Number(currentPromptInfo?.promptId);
      if (Number.isFinite(normalizedPromptId) && normalizedPromptId > 0) {
        openModalWithDefaultSelectedApproachId(normalizedPromptId);
      } else {
        openModalWithDefaultSelectedApproachId(undefined);
      }
    }
  }, [currentPromptInfo?.isCustom, currentPromptInfo?.promptId, openModalWithDefaultSelectedApproachId]);
  const fullScreenContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="dbl-ai-analysis-card-wrap" ref={fullScreenContentRef}>
      <CollapseCard
        className="dbl-ai-analysis-card dbl-hover-scrollbar-area"
        title={(
          <div className="dbl-ai-analysis-card__title dbl-card-drag-title">
            {isEdit ? <SvgIcon type="drag-icon" /> : null}
            <img src={robotSvg} alt="robot" width={20} height={20} />
            <span className="dbl-ai-analysis-card__title-text">{formatMessage({ id: 'dbl_ai_analysis_card_title' })}</span>
          </div>
        )}
        extra={
          <>
            <AiAnalysisCardExtra
              isEdit={Boolean(isEdit)}
              promptOptions={promptOptions}
              promptId={promptId}
              currentPromptInfo={currentPromptInfo}
              hasConversation={hasConversation}
              formatMessage={formatMessage}
              onEditApproach={handleEditApproach}
              onSelectApproach={handleSelectApproach}
              onRefreshClick={handleRunAnalysis}
              onEditPresetValue={handleEditPresetValue}
              onDeleteCard={handleDeleteCard}
              fullScreenContentRef={fullScreenContentRef}
              onFullScreenExit={onCloseAiAnalysisModal}
            />
          </>
        }
        collapsed={false}
        onCollapseChange={() => {}}
      >
        <div className="dbl-ai-analysis-card__content">
          {isFilterChangeTipVisible ? (
            <div className="dbl-ai-analysis-card__filter-change-tip">
              <SvgIcon type="alert-circle-tips" className="dbl-ai-analysis-card__filter-change-tip-icon" />
              <span className="dbl-ai-analysis-card__filter-change-tip-text">
                {formatMessage({ id: 'dbl_ai_analysis_filter_change_tip_prefix' })}
                <Button
                  type="link"
                  className="dbl-ai-analysis-card__filter-change-tip-action"
                  onClick={() => {
                    handleRunAnalysis().catch(() => {});
                  }}
                >
                  {formatMessage({ id: 'dbl_ai_analysis_filter_change_tip_action' })}
                </Button>
                {formatMessage({ id: 'dbl_ai_analysis_filter_change_tip_suffix' })}
              </span>
            </div>
          ) : null}
          <AIAnalysisBox
            session={displaySession}
            hasAnalyzableCharts={hasAnalyzableCharts}
            onRunAnalysis={handleRunAnalysis}
            onOpenAnalysisApproach={handleOpenAnalysisApproach}
            onStopClick={handleStopSession}
          />
        </div>
      </CollapseCard>
    </div>
  );
};

export default memo(AiAnalysisCardView);

